import { useMsal } from "@azure/msal-react";
import {
    ActionIcon,
    Anchor,
    Badge,
    Box,
    Button,
    Divider,
    Flex,
    Group,
    Loader,
    Modal,
    SimpleGrid,
    Stack,
    Text,
    Textarea,
    Title,
    Tooltip,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { IconExternalLink, IconTrash } from "@tabler/icons-react";
import { type InfiniteData, useQueryClient } from "@tanstack/react-query";
import {
    MantineReactTable,
    type MRT_ColumnDef,
    type MRT_ColumnFiltersState,
    type MRT_SortingState,
    useMantineReactTable,
} from "mantine-react-table";
import { useMemo, useRef, useState } from "react";
import { DatasourceBadges } from "src/components/adminBadges";
import {
    type AuthSearchResponse,
    useAuthSearchPagination,
} from "src/hooks/useAuthSearchPagination";
import { deleteAPI, fetchWithAuth, postAPI } from "src/utils/fetch";
import { useAdmin } from "src/utils/useAdmin";
import {
    CopyableField,
    RoleSelectionComponent,
    scdPersonUrl,
    type User,
    useAuthDatasources,
    useRoles,
    useUserOperations,
} from "./manageUsers";

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;

const REQUEST_STATUS_COLORS: Record<string, string> = {
    pending: "yellow",
    approved: "green",
    rejected: "red",
};

export type Request = {
    id: string;
    user_id: string;
    user_email: string;
    request_datetime: number;
    requested_datasources: { id: string }[];
    status: string;
    reason: string;
    rejection_reason?: string | null;
};

interface QueryParams {
    columnFilters: MRT_ColumnFiltersState;
    globalFilter: string;
}

interface UpdateRequestStatusParams {
    // The backend keys the PATCH by the request id; it also picks the cached row to update.
    request_id: string;
    user_id: string;
    updated_status: string;
    rejection_reason: string | null;
}

// The search index behind /access-requests/search catches up with a PATCH / DELETE
// asynchronously, so a refetch fired straight away still returns the old status.
const SEARCH_INDEX_SETTLE_MS = 2000;

// Create a custom hook for request operations
const useRequestOperations = (params: QueryParams) => {
    const isAdmin = useAdmin();
    const { instance } = useMsal();
    const queryClient = useQueryClient();
    const { columnFilters, globalFilter } = params;

    const requestsQuery = useAuthSearchPagination<Request>({
        queryKey: ["access-requests"],
        path: "v1/auth/access-requests/search",
        columnFilters,
        globalFilter,
        globalFilterKey: "user_email",
        maxResults: 25,
        enabled: isAdmin,
    });

    // Applies a change to one request in every cached page right away: `update` returns the
    // new row, or null to drop it (e.g. deleted, or no longer matching the status filter),
    // in which case Total is decremented too.
    const updateCachedRequest = (
        request_id: string,
        update: (request: Request) => Request | null,
    ) => {
        queryClient.setQueriesData<InfiniteData<AuthSearchResponse<Request>>>(
            { queryKey: ["access-requests"] },
            (data) => {
                if (!data) return data;
                let removed = 0;
                const pages = data.pages.map((page) => {
                    const hits = page.hits.hits.flatMap((hit) => {
                        if (hit._source.id !== request_id) return [hit];
                        const next = update(hit._source);
                        if (!next) {
                            removed += 1;
                            return [];
                        }
                        return [{ ...hit, _source: next }];
                    });
                    return { ...page, hits: { ...page.hits, hits } };
                });
                if (removed > 0 && pages[0]) {
                    pages[0] = {
                        ...pages[0],
                        hits: {
                            ...pages[0].hits,
                            total: { value: Math.max(0, pages[0].hits.total.value - removed) },
                        },
                    };
                }
                return { ...data, pages };
            },
        );

        // Reconcile with the server once the index has caught up.
        setTimeout(() => {
            queryClient.invalidateQueries({ queryKey: ["access-requests"] });
        }, SEARCH_INDEX_SETTLE_MS);
    };

    // PATCH operation
    const updateRequest = async ({
        request_id,
        user_id,
        updated_status,
        rejection_reason,
    }: UpdateRequestStatusParams) => {
        const response = await fetchWithAuth({
            url: `${BACKEND_URL}/v1/auth/access-requests`,
            method: "PATCH",
            payload: { id: request_id, user_id, updated_status, rejection_reason },
            instance,
        });

        if (!response?.ok) {
            throw new Error("Failed to update request status");
        }

        // When the active status filter no longer matches (e.g. default "pending" after an
        // approval) the row leaves the table instead of lingering with a stale badge.
        const statusFilter = columnFilters.find((filter) => filter.id === "status")?.value;
        const stillMatchesFilter =
            !statusFilter ||
            (Array.isArray(statusFilter)
                ? statusFilter.length === 0 || statusFilter.includes(updated_status)
                : statusFilter === updated_status);
        updateCachedRequest(request_id, (request) =>
            stillMatchesFilter ? { ...request, status: updated_status, rejection_reason } : null,
        );

        return response;
    };

    // DELETE operation
    const deleteRequest = async (request_id: string) => {
        const response = await deleteAPI({
            url: `${BACKEND_URL}/v1/auth/access-requests/${encodeURIComponent(request_id)}`,
            instance,
        });

        if (!response?.ok) {
            throw new Error("Failed to delete the request");
        }

        updateCachedRequest(request_id, () => null);

        return response;
    };

    return { requestsQuery, updateRequest, deleteRequest };
};

// Users are only needed one at a time - when an admin opens a request - so look the
// requester up on demand instead of paging through the whole directory on load.
const useUserLookup = () => {
    const { instance } = useMsal();

    return async (userId: string): Promise<User | null> => {
        const url = new URL("v1/auth/users/search", BACKEND_URL);
        url.searchParams.set("max_results", "10");
        const response = await postAPI({
            url: url.toString(),
            instance,
            payload: [{ key: "id", value: userId }],
        });
        const data: AuthSearchResponse<User> = await response.json();
        const users = (data.hits?.hits ?? []).map((hit) => hit._source);
        return users.find((user) => user.id === userId) ?? users[0] ?? null;
    };
};

const ManageRequests = () => {
    // Roles load once per visit; the request modal's role list reuses the cache.
    const {
        data: roles,
        isLoading: isLoadingRoles,
        refetch: fetchRoles,
    } = useRoles({ refreshOnPageLoad: true });
    // Load the datasource catalog once per visit; the request modal reuses the cache.
    const { data: authDatasources = [] } = useAuthDatasources({ refreshOnPageLoad: true });
    // Use stable empty array reference to prevent unnecessary re-renders
    const stableRoles = useMemo(() => roles ?? [], [roles]);

    const datasourceDescriptions = useMemo(() => {
        const list = Array.isArray(authDatasources) ? authDatasources : [];
        return Object.fromEntries(list.map((ds) => [ds.id, ds.description || ds.name || ""]));
    }, [authDatasources]);

    const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([
        { id: "status", value: "pending" },
    ]);
    const [globalFilter, setGlobalFilter] = useState("");
    const [sorting, setSorting] = useState<MRT_SortingState>([]);

    const { updateUser } = useUserOperations({
        columnFilters: [],
        globalFilter: "",
        enabled: false,
    });

    const lookupUser = useUserLookup();
    const [isLoadingUser, setIsLoadingUser] = useState(false);

    const columns = useMemo<MRT_ColumnDef<Request>[]>(
        () => [
            {
                id: "scd_link",
                header: "",
                accessorFn: (row) => row?.user_id ?? "",
                Cell: ({ cell }) => {
                    const id = cell.getValue<string>();
                    if (!id) return null;
                    return (
                        <Anchor
                            href={scdPersonUrl(id)}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            aria-label={`Open ${id} in SCD`}
                            style={{ cursor: "pointer" }}
                        >
                            <IconExternalLink size={16} />
                        </Anchor>
                    );
                },
                enableSorting: false,
                enableColumnFilter: false,
                enableClickToCopy: false,
                size: 40,
                maxSize: 40,
                minSize: 40,
                enableResizing: false,
            },
            {
                accessorKey: "user_id" as const,
                header: "User ID",
                accessorFn: (row) => row?.user_id ?? "",
            },
            {
                accessorKey: "user_email" as const,
                header: "User Email",
                accessorFn: (row) => row?.user_email ?? "",
            },
            {
                accessorKey: "request_datetime" as const,
                header: "Requested at",
                accessorFn: (row) =>
                    row ? new Date(1000 * row.request_datetime).toLocaleString() : "",
            },
            {
                accessorKey: "requested_datasources" as const,
                header: "Requested Datasources",
                accessorFn: (row) =>
                    (row?.requested_datasources ?? []).map((ds) => ds.id).join(", "),
                Cell: ({ row }) => (
                    <DatasourceBadges
                        items={(row.original.requested_datasources ?? []).map((ds) => ds.id)}
                        descriptions={datasourceDescriptions}
                    />
                ),
            },
            {
                accessorKey: "status" as const,
                header: "Status",
                accessorFn: (row) => row?.status ?? "",
                Cell: ({ cell }) => {
                    const status = cell.getValue<string>();
                    if (!status) return null;
                    return (
                        <Badge variant="light" color={REQUEST_STATUS_COLORS[status]} size="sm">
                            {status}
                        </Badge>
                    );
                },
            },
        ],
        [datasourceDescriptions],
    );

    const {
        requestsQuery: {
            data: requests,
            isLoading: isLoadingRequests,
            isFetching,
            isFetchingNextPage,
            hasNextPage,
            fetchNextPage,
        },
        updateRequest,
        deleteRequest,
    } = useRequestOperations({
        columnFilters,
        globalFilter,
    });

    const fetchedRequests = useMemo(() => {
        if (!requests?.pages) return [];
        const seen = new Set<string>();
        return requests.pages
            .flatMap((page) => page.hits.hits)
            .filter((hit) => {
                if (seen.has(hit._id)) return false;
                seen.add(hit._id);
                return true;
            })
            .map((hit) => hit._source);
    }, [requests]);

    const totalRequests = requests?.pages[0]?.hits.total.value ?? fetchedRequests.length;

    const tableContainerRef = useRef<HTMLDivElement>(null);

    const fetchMoreOnBottomReached = (containerRefElement?: HTMLDivElement) => {
        if (containerRefElement) {
            const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
            if (
                scrollHeight - scrollTop - clientHeight < 400 &&
                !isFetchingNextPage &&
                hasNextPage
            ) {
                fetchNextPage();
            }
        }
    };

    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [selectedRequest, setSelectedRequest] = useState<Request | null>(null);
    const requestedDatasourceIds = useMemo(
        () => (selectedRequest?.requested_datasources ?? []).map((ds) => ds.id),
        [selectedRequest],
    );

    const [modalOpened, setModalOpened] = useState(false);
    const [isSubmittingApprove, setIsSubmittingApprove] = useState(false);
    const [isSubmittingReject, setIsSubmittingReject] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const [roleSearchTerm, setRoleSearchTerm] = useState("");

    const [rejectRequestModalOpened, { open: openRejectRequest, close: closeRejectRequest }] =
        useDisclosure(false);
    const [rejectionReason, setRejectionReason] = useState<string>("");
    const [deleteRequestModalOpened, { open: openDeleteRequest, close: closeDeleteRequest }] =
        useDisclosure(false);

    const form = useForm({
        initialValues: {
            user_id: "",
            user_email: "",
            request_datetime: 0,
            requested_datasources: [] as { id: string }[],
            status: "",
            reason: "",
            roles: [] as string[],
        },
        validate: {
            roles: (value) => (value.length === 0 ? "At least one role is required" : null),
        },
    });

    const handleOpenModal = async (request: Request) => {
        if (isLoadingRoles) {
            notifications.show({
                title: "Loading",
                message: "Please wait while roles are being loaded...",
                loading: true,
            });
            return;
        }

        if (stableRoles.length === 0) {
            fetchRoles();
        }

        // Open right away with what the request itself carries; the requester's current
        // roles / org code arrive from the on-demand user lookup below.
        setSelectedRequest(request);
        setSelectedUser(null);
        // Role picker is narrowed to roles that grant a requested datasource; search
        // still filters within that subset. Reset the term on every open so a previous
        // request's term never leaks into the next.
        setRoleSearchTerm("");
        form.setValues({
            user_id: request.user_id,
            user_email: request.user_email,
            request_datetime: request.request_datetime,
            requested_datasources: request.requested_datasources,
            status: request.status,
            reason: request.reason,
            roles: [],
        });
        setModalOpened(true);

        setIsLoadingUser(true);
        try {
            const user = await lookupUser(request.user_id);
            if (!user) {
                throw new Error("User not found for this request");
            }
            setSelectedUser(user);
            form.setFieldValue("roles", user.roles);
        } catch (error) {
            notifications.show({
                title: "Error",
                message:
                    error instanceof Error ? error.message : "Failed to load the requesting user",
                color: "red",
            });
            setModalOpened(false);
        } finally {
            setIsLoadingUser(false);
        }
    };

    const handleCloseModal = () => {
        setModalOpened(false);
        form.reset();
        setRoleSearchTerm("");
    };

    // Once a request is approved or rejected it is final - the admin can only review it.
    // The role list is limited to roles that grant at least one requested datasource.
    const isRequestPending = selectedRequest?.status === "pending";

    const handleApprove = async (user_id: string, roleKeys: string[]) => {
        setIsSubmittingApprove(true);
        try {
            if (!selectedUser) {
                throw new Error("No user selected");
            }

            const updatedUser = await updateUser({
                data: {
                    id: selectedUser.id,
                    email: selectedUser.email,
                    org_code: selectedUser.org_code,
                    roles: roleKeys,
                },
            });

            setSelectedUser(updatedUser);

            await updateRequest({
                request_id: selectedRequest?.id ?? "",
                user_id: user_id,
                updated_status: "approved",
                rejection_reason: null,
            });
            notifications.show({
                title: "Success",
                message: "Request approved successfully",
                color: "green",
            });
            handleCloseModal();
        } catch (error) {
            notifications.show({
                title: "Error",
                message: error instanceof Error ? error.message : "Failed to approve the request",
                color: "red",
            });
        } finally {
            setIsSubmittingApprove(false);
        }
    };

    const handleReject = async (user_id: string) => {
        setIsSubmittingReject(true);
        closeRejectRequest();
        try {
            if (!selectedUser) {
                throw new Error("No user selected");
            }
            await updateRequest({
                request_id: selectedRequest?.id ?? "",
                user_id: user_id,
                updated_status: "rejected",
                rejection_reason: rejectionReason,
            });
            notifications.show({
                title: "Success",
                message: "Request rejected successfully",
                color: "green",
            });
            handleCloseModal();
            setRejectionReason("");
            closeRejectRequest();
        } catch (error) {
            notifications.show({
                title: "Error",
                message: error instanceof Error ? error.message : "Failed to reject the request",
                color: "red",
            });
        } finally {
            setIsSubmittingReject(false);
        }
    };

    // Deleting is allowed whatever the status - e.g. to clear duplicates or stale requests.
    const handleDelete = async () => {
        if (!selectedRequest) return;
        setIsDeleting(true);
        try {
            await deleteRequest(selectedRequest.id);
            notifications.show({
                title: "Success",
                message: "Request deleted successfully",
                color: "green",
            });
            closeDeleteRequest();
            handleCloseModal();
        } catch (error) {
            notifications.show({
                title: "Error",
                message: error instanceof Error ? error.message : "Failed to delete the request",
                color: "red",
            });
        } finally {
            setIsDeleting(false);
        }
    };

    const table = useMantineReactTable<Request>({
        layoutMode: "grid",
        columns,
        data: fetchedRequests ?? [],
        enableColumnOrdering: true,
        enablePinning: true,
        enableBottomToolbar: false,
        renderToolbarAlertBannerContent: () => <></>,
        renderTopToolbarCustomActions: () => (
            <Flex align="center" gap="md">
                <Text size="sm" c="dimmed">
                    {`Total: ${totalRequests.toLocaleString()}, (Showing ${table.getFilteredRowModel().rows.length.toLocaleString()})`}
                </Text>
                {isFetching && <Loader size="xs" />}
            </Flex>
        ),
        enableColumnResizing: true,
        enableFilters: true,
        enablePagination: false,
        enableRowNumbers: true,
        enableClickToCopy: false,
        getRowId: (row: Request) => row.id,
        mantineToolbarAlertBannerProps: {
            style: {
                display: "none",
                background: "#f0f0f0",
                fontWeight: "bold",
            },
        },
        onColumnFiltersChange: setColumnFilters,
        onGlobalFilterChange: setGlobalFilter,
        onSortingChange: setSorting,
        enableSelectAll: false,
        selectAllMode: "page",
        enableGrouping: true,
        enableDensityToggle: false,
        enableStickyHeader: true,
        mantineTableContainerProps: {
            ref: tableContainerRef,
            style: { maxHeight: "calc(100vh - 280px)" },
            onScroll: (event) => fetchMoreOnBottomReached(event.currentTarget),
        },
        mantineTableBodyRowProps: ({ row }) => ({
            onClick: () => handleOpenModal(row.original),
            sx: { cursor: "pointer" },
        }),
        state: {
            isLoading: isLoadingRequests || isLoadingRoles,
            showProgressBars: isFetchingNextPage,
            columnFilters,
            globalFilter,
            sorting,
        },
        initialState: {
            showGlobalFilter: true,
            density: "xs",
            showColumnFilters: true,
        },
    });
    return (
        <>
            <Flex px="xl" pb="xl" direction="column" gap="md">
                <Flex gap="md" align="center">
                    <Title lh={2} c="purple.8" order={2} style={{ textTransform: "uppercase" }}>
                        Manage Requests
                    </Title>
                </Flex>
            </Flex>

            <MantineReactTable table={table} />
            <Modal
                opened={modalOpened}
                onClose={handleCloseModal}
                title={`Manage User Access Request`}
                size="xl"
                styles={{
                    body: {
                        minHeight: 500,
                    },
                }}
            >
                <form>
                    <Stack gap="md">
                        <SimpleGrid cols={3}>
                            <CopyableField
                                label="Email"
                                value={selectedUser?.email || selectedRequest?.user_email || ""}
                            />
                            <CopyableField
                                label="User ID"
                                value={selectedUser?.id || selectedRequest?.user_id || ""}
                                externalHref={
                                    selectedUser?.id || selectedRequest?.user_id
                                        ? scdPersonUrl(
                                              selectedUser?.id || selectedRequest?.user_id || "",
                                          )
                                        : undefined
                                }
                            />
                            <CopyableField label="Org Code" value={selectedUser?.org_code || ""} />
                        </SimpleGrid>

                        <Divider />

                        <Flex gap="md" align="stretch">
                            <Stack flex={1} gap="md" data-testid="request-details-panel">
                                {selectedRequest?.requested_datasources?.length > 0 && (
                                    <Box>
                                        <Text size="sm" fw={500} mb={4}>
                                            Requested Datasources
                                        </Text>
                                        <DatasourceBadges
                                            items={selectedRequest.requested_datasources.map(
                                                (ds) => ds.id,
                                            )}
                                            descriptions={datasourceDescriptions}
                                        />
                                    </Box>
                                )}

                                <Textarea
                                    label="Reason"
                                    value={selectedRequest?.reason || ""}
                                    readOnly
                                    autosize
                                    minRows={2}
                                />
                                {selectedRequest?.status === "rejected" && (
                                    <Textarea
                                        label="Rejection reason"
                                        value={
                                            selectedRequest.rejection_reason ||
                                            "No rejection reason was provided."
                                        }
                                        readOnly
                                        autosize
                                        minRows={2}
                                        data-testid="request-rejection-reason"
                                    />
                                )}
                                <Group gap="sm" align="center">
                                    <Badge
                                        variant="light"
                                        color={REQUEST_STATUS_COLORS[selectedRequest?.status ?? ""]}
                                        data-testid="request-status-badge"
                                    >
                                        {selectedRequest?.status || "unknown"}
                                    </Badge>
                                    <Text size="sm" c="dimmed">
                                        Requested at{" "}
                                        {new Date(
                                            1000 * (selectedRequest?.request_datetime || 0),
                                        ).toLocaleString()}
                                    </Text>
                                </Group>
                            </Stack>

                            <Divider orientation="vertical" />

                            <Box flex={1} data-testid="request-roles-panel">
                                {isLoadingUser ? (
                                    <Group justify="center" py="xl">
                                        <Loader size="sm" />
                                        <Text size="sm" c="dimmed">
                                            Loading current roles...
                                        </Text>
                                    </Group>
                                ) : (
                                    <RoleSelectionComponent
                                        form={form}
                                        roleSearchTerm={roleSearchTerm}
                                        setRoleSearchTerm={setRoleSearchTerm}
                                        datasourceIdsFilter={requestedDatasourceIds}
                                    />
                                )}
                            </Box>
                        </Flex>

                        <Group justify="flex-end" mt="md">
                            <Tooltip label="Delete request">
                                <ActionIcon
                                    mr="auto"
                                    size="lg"
                                    variant="outline"
                                    color="red"
                                    aria-label="Delete request"
                                    onClick={openDeleteRequest}
                                    loading={isDeleting}
                                    disabled={isSubmittingApprove || isSubmittingReject}
                                >
                                    <IconTrash size={18} />
                                </ActionIcon>
                            </Tooltip>
                            <Button type="button" variant="outline" onClick={handleCloseModal}>
                                Cancel
                            </Button>
                            <Button
                                color="green"
                                type="button"
                                variant="outline"
                                onClick={() =>
                                    handleApprove(selectedRequest?.user_id || "", form.values.roles)
                                }
                                loading={isSubmittingApprove}
                                disabled={isLoadingUser || !isRequestPending}
                            >
                                Approve
                            </Button>
                            <Button
                                color="red"
                                type="button"
                                variant="outline"
                                onClick={openRejectRequest}
                                loading={isSubmittingReject}
                                disabled={isLoadingUser || !isRequestPending}
                            >
                                Reject
                            </Button>
                            <Modal
                                size="lg"
                                opened={rejectRequestModalOpened}
                                onClose={closeRejectRequest}
                                title="Provide a rejection reason"
                            >
                                <Stack align="flex-end">
                                    <Textarea
                                        placeholder="Please as an admin explain why you are rejecting this request"
                                        value={rejectionReason}
                                        onChange={(e) => setRejectionReason(e.target.value)}
                                        style={{ width: "100%" }}
                                        minRows={4}
                                        required
                                    />
                                    <Button
                                        onClick={() => handleReject(selectedRequest?.user_id || "")}
                                        disabled={!rejectionReason.trim()}
                                        color="red"
                                    >
                                        Reject
                                    </Button>
                                </Stack>
                            </Modal>
                            <Modal
                                size="sm"
                                opened={deleteRequestModalOpened}
                                onClose={closeDeleteRequest}
                                title="Confirm Request Deletion"
                            >
                                <Stack gap="md">
                                    <Text>
                                        Are you sure you want to delete the access request from{" "}
                                        <b>{selectedRequest?.user_email}</b>? This cannot be undone.
                                    </Text>
                                    <Group justify="flex-end">
                                        <Button variant="outline" onClick={closeDeleteRequest}>
                                            Cancel
                                        </Button>
                                        <Button
                                            color="red"
                                            loading={isDeleting}
                                            onClick={handleDelete}
                                        >
                                            Delete
                                        </Button>
                                    </Group>
                                </Stack>
                            </Modal>
                        </Group>
                    </Stack>
                </form>
            </Modal>
        </>
    );
};

export default ManageRequests;
